import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Phone, Calculator, CheckCircle, Truck, Leaf } from 'lucide-react';

interface BlogCTAProps {
  variant?: 'mid-article' | 'full' | 'sticky';
}

const BlogCTA: React.FC<BlogCTAProps> = ({ variant = 'full' }) => {
  const { language } = useLanguage();

  if (variant === 'mid-article') {
    return (
      <Card className="my-8 bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
        <CardContent className="p-6 text-center">
          <h3 className="text-xl font-bold text-foreground mb-3">
            {language === 'en' 
              ? 'Need help choosing the right materials?' 
              : '¿Necesitas ayuda para elegir los materiales correctos?'
            }
          </h3>
          <p className="text-muted-foreground mb-4">
            {language === 'en'
              ? 'Our team has 15+ years of experience helping Texas homeowners.'
              : 'Nuestro equipo tiene 15+ años de experiencia ayudando a propietarios de Texas.'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={`/${language}/${language === 'en' ? 'quote' : 'cotizar'}`}>
              <Button size="lg" className="w-full sm:w-auto">
                <Calculator className="mr-2 h-4 w-4" />
                {language === 'en' ? 'Get a Free Quote' : 'Cotiza Gratis'}
              </Button>
            </Link>
            <a href="tel:+19363200763">
              <Button variant="outline" size="lg" className="w-full sm:w-auto">
                <Phone className="mr-2 h-4 w-4" />
                (936) 320-0763
              </Button>
            </a>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (variant === 'sticky') {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background border-t border-border shadow-lg md:hidden">
        <div className="flex gap-2">
          <Link to={`/${language}/${language === 'en' ? 'quote' : 'cotizar'}`} className="flex-1">
            <Button className="w-full" size="lg">
              <Calculator className="mr-2 h-4 w-4" />
              {language === 'en' ? 'Get Quote' : 'Cotizar'}
            </Button>
          </Link>
          <a href="tel:+19363200763">
            <Button variant="outline" size="lg">
              <Phone className="h-4 w-4" />
            </Button>
          </a>
        </div>
      </div>
    );
  }

  // Full variant
  return (
    <Card className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground overflow-hidden">
      <CardContent className="p-8">
        <div className="text-center">
          <Leaf className="h-12 w-12 mx-auto mb-4 text-primary-foreground/80" />
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            {language === 'en' 
              ? 'Ready to Transform Your Lawn?' 
              : '¿Listo para Transformar tu Jardín?'
            }
          </h3>
          
          <div className="flex flex-wrap justify-center gap-4 mb-6 text-sm">
            <div className="flex items-center">
              <CheckCircle className="h-4 w-4 mr-2" />
              {language === 'en' ? 'Fresh Sod' : 'Césped Fresco'}
            </div>
            <div className="flex items-center">
              <Truck className="h-4 w-4 mr-2" />
              {language === 'en' ? 'Same-Day Delivery' : 'Entrega Mismo Día'}
            </div>
            <div className="flex items-center">
              <Calculator className="h-4 w-4 mr-2" />
              {language === 'en' ? 'Free Quotes' : 'Cotización Gratis'}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+19363200763">
              <Button variant="secondary" size="lg" className="w-full sm:w-auto text-lg">
                <Phone className="mr-2 h-5 w-5" />
                (936) 320-0763
              </Button>
            </a>
            <Link to={`/${language}/${language === 'en' ? 'quote' : 'cotizar'}`}>
              <Button 
                variant="outline" 
                size="lg" 
                className="w-full sm:w-auto text-lg border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
              >
                <Calculator className="mr-2 h-5 w-5" />
                {language === 'en' ? 'Get Your Quote Online' : 'Cotiza en Línea'}
              </Button>
            </Link>
          </div>

          <p className="mt-6 text-sm text-primary-foreground/80">
            {language === 'en'
              ? 'Serving Montgomery County, The Woodlands, Magnolia, Conroe & surrounding areas'
              : 'Sirviendo Condado Montgomery, The Woodlands, Magnolia, Conroe y áreas circundantes'
            }
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default BlogCTA;
